import { FC, useMemo } from "react";

import { useLocalization } from "@/hooks";
import { QuestionResults, Section } from "@/types/resources";
import PriceElasticityReport, {
  PriceElasticityReportData,
  PriceSegmentKey,
} from "./PriceElasticityReport";

interface PriceElasticitySectionResultsProps {
  data: QuestionResults[];
  section: Section;
}

/* ── Cooker ─────────────────────────────────────────────── */

/**
 * The Van Westendorp template lays out four `pricing` questions in
 * a fixed order: too cheap, bargain, expensive, too expensive. We
 * trust the templated section's question ordering rather than
 * trying to identify each question by text (which would break
 * across locales). The cooker maps each pricing question's flat
 * list of price answers to a segment key and forwards them to the
 * report — the curves and intersections are computed there.
 *
 * Currency is read from the first pricing question's `currency`
 * field; falls back to USD if missing.
 */
const VW_SEGMENT_ORDER: PriceSegmentKey[] = [
  "too_cheap",
  "bargain",
  "expensive",
  "too_expensive",
];

function cookPriceElasticityData(
  data: QuestionResults[] | undefined
): PriceElasticityReportData {
  const responses: Record<PriceSegmentKey, number[]> = {
    bargain: [],
    expensive: [],
    too_cheap: [],
    too_expensive: [],
  };

  const pricingQuestions = (data ?? []).filter(
    (r) => r?.question?.type === "pricing"
  );

  let currencyCode: string | null = null;

  pricingQuestions.slice(0, VW_SEGMENT_ORDER.length).forEach((q, idx) => {
    const segmentKey = VW_SEGMENT_ORDER[idx];
    (q.groups ?? []).forEach((g) => {
      (g.answers ?? []).forEach((a) => {
        if (a.answer.type === "pricing" && typeof a.answer.price === "number") {
          responses[segmentKey].push(a.answer.price);
          /* Currency lives on each pricing answer. We pick the first one we
             see and stick with it — all answers in a section share the
             persona's currency, so this is a faithful proxy. */
          if (!currencyCode && a.answer.currency) {
            currencyCode = a.answer.currency;
          }
        }
      });
    });
  });

  return { currencyCode: currencyCode ?? "USD", responses };
}

/* ── Component ──────────────────────────────────────────── */

const PriceElasticitySectionResults: FC<PriceElasticitySectionResultsProps> = ({
  data,
}) => {
  const { browserLocale } = useLocalization();
  const reportData: PriceElasticityReportData = useMemo(() => {
    const cooked = cookPriceElasticityData(data);
    /* If the answers didn't carry a currency (sample/empty state), fall
       back to the browser's currency so the chart still formats prices
       in a sensible way. */
    const hasPricingCurrency = (data ?? []).some((r) =>
      (r?.groups ?? []).some((g) =>
        (g.answers ?? []).some(
          (a) => a.answer.type === "pricing" && a.answer.currency
        )
      )
    );
    if (!hasPricingCurrency) {
      return {
        ...cooked,
        currencyCode: browserLocale.currencyCode ?? cooked.currencyCode,
      };
    }
    return cooked;
  }, [data, browserLocale.currencyCode]);

  return <PriceElasticityReport data={reportData} />;
};

export default PriceElasticitySectionResults;
